// OIDC token access for the SPA. The browser holds the access token in
// memory (seeded from oidc-client-ts' session store) and the server validates
// it on every API call. Sign-out ends a real Zitadel session via the OIDC
// end-session endpoint; local dev-login sessions clear locally instead.

import { useSyncExternalStore } from 'react'
import type { User, UserManager } from 'oidc-client-ts'
import {
  devSignin,
  getUserManager,
  initOidc,
  signoutRedirect as oidcSignoutRedirect,
  storeMintedSession,
} from './oidc'
import { ApiError } from './api'
import { endHubSession } from './signalr/hub-session'

export { tryRecoverAuth } from './api'

type Me = {
  userId: string
  email: string | null
  name: string
  entityId?: string
  permissions?: string[]
  impersonation?: {
    actorUserId: string
    actorName: string
    actorEmail: string | null
  }
}

const listeners = new Set<() => void>()

export type MeLoader = (entityId?: string) => Promise<Me>

const setupAuthNotCalled: MeLoader = () =>
  Promise.reject(new Error('hydrateIdentity called before setupAuth'))
let loadMe: MeLoader = setupAuthNotCalled

function notify(): void {
  for (const l of listeners) l()
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

let oidcAccessToken: string | null = null
let tokenContext: { generation: number; principal?: string } = { generation: 0 }
let cachedMe: Me | null = null
let sessionEpoch = 0
let scopedSeq = 0

function replaceToken(token: string | null, profile?: User['profile']): void {
  oidcAccessToken = token
  tokenContext = {
    generation: tokenContext.generation + 1,
    principal: profile?.iss && profile.sub ? JSON.stringify([profile.iss, profile.sub]) : undefined,
  }
}

function clearSession(): void {
  replaceToken(null)
  cachedMe = null
  sessionEpoch++
  notify()
}

// Profile-only renewals omit entity permissions and must not replace a newer
// scoped response. Logout invalidates both kinds of in-flight response.
export function hydrateIdentity(entityId?: string): Promise<void> {
  const startedEpoch = sessionEpoch
  const startedScopedSeq = entityId ? ++scopedSeq : scopedSeq
  return loadMe(entityId)
    .then((me) => {
      if (startedEpoch !== sessionEpoch) return
      if (entityId && startedScopedSeq !== scopedSeq) return
      cachedMe = cachedMe
        ? {
            ...cachedMe,
            ...me,
            entityId: me.entityId ?? cachedMe.entityId,
            permissions: me.permissions ?? cachedMe.permissions,
          }
        : me
      notify()
    })
    .catch((e) => {
      if (startedEpoch !== sessionEpoch) return
      if (entityId && startedScopedSeq !== scopedSeq) return
      if (e instanceof ApiError && e.status === 401) {
        cachedMe = null
        notify()
        return
      }
      console.warn('[auth] /me hydration failed; keeping cached identity', e)
    })
}

export function bindOidcEvents(userManager: UserManager, initialUser: User | null): void {
  if (initialUser && !initialUser.expired) {
    replaceToken(initialUser.access_token ?? null, initialUser.profile)
    void hydrateIdentity()
  }

  userManager.events.addUserLoaded((user) => {
    replaceToken(user.access_token ?? null, user.profile)
    notify()
    void hydrateIdentity()
  })
  userManager.events.addUserUnloaded(() => clearSession())
  userManager.events.addAccessTokenExpired(() => clearSession())
  userManager.events.addSilentRenewError((e) => {
    console.warn('[auth] silent renew failed', e)
  })

  notify()
}

export async function devLogin(email: string): Promise<void> {
  const token = await devSignin(email)
  replaceToken(token)
  notify()
  await hydrateIdentity()
}

// Accepted tradeoff: a minted session has no refresh token and no Zitadel browser
// cookie, so it can't silent-renew. The invitee is bounced to login once when it
// expires (~12h), and signs in normally with the password they just set.
export async function applyMintedSession(
  accessToken: string,
  expiresInSeconds: number,
): Promise<void> {
  await storeMintedSession(accessToken, expiresInSeconds)
  replaceToken(accessToken)
  notify()
  await hydrateIdentity()
}

export async function setupAuth(meLoader: MeLoader): Promise<void> {
  loadMe = meLoader
  const result = await initOidc()
  if (!result.available) return
  let initialUser = await result.userManager.getUser().catch((e) => {
    console.warn('[auth] userManager.getUser() failed; treating as logged out', e)
    return null
  })

  if (initialUser?.expired && (initialUser.refresh_token || initialUser.id_token)) {
    initialUser = await result.userManager.signinSilent().catch((e) => {
      console.warn('[auth] persisted session renewal failed; treating as logged out', e)
      return null
    })
  }

  bindOidcEvents(result.userManager, initialUser)
}

export function getToken(): string | null {
  return oidcAccessToken
}

export function getMe(): Me | null {
  return cachedMe
}

export function fetchIdentity(entityId?: string): Promise<Me> {
  return loadMe(entityId)
}

function getTokenContext(): typeof tokenContext {
  return tokenContext
}

export function getSessionEpoch(): number {
  return sessionEpoch
}

export function resetIdentityForSwitch(): void {
  sessionEpoch++
  if (!cachedMe) return
  cachedMe = {
    ...cachedMe,
    entityId: undefined,
    permissions: undefined,
    impersonation: undefined,
  }
  notify()
}

// Resolves true when it triggered an end-session redirect to Zitadel (the page
// is navigating away, so the caller need not route); false when it only cleared
// locally and the caller must send the user to /auth/login.
export async function clearToken(): Promise<boolean> {
  clearSession()
  // The hub cookie outlives the bearer session unless the server deletes it.
  await endHubSession().catch((e: unknown) => {
    console.warn('[auth] failed to clear the hub session', e)
  })
  const um = getUserManager()
  if (!um) return false
  // Dev-login sessions have no OIDC session to end, so sign out locally.
  const user = await um.getUser()
  if (user?.id_token) {
    await oidcSignoutRedirect()
    return true
  }
  await um.removeUser()
  return false
}

export function useAuth(): {
  isAuthenticated: boolean
  token: string | null
  tokenContext: typeof tokenContext
  user: Me | null
} {
  const token = useSyncExternalStore(subscribe, getToken, getToken)
  const context = useSyncExternalStore(subscribe, getTokenContext, getTokenContext)
  const user = useSyncExternalStore(subscribe, getMe, getMe)
  return { isAuthenticated: token !== null, token, tokenContext: context, user }
}

export function __resetAuthForTests(
  meLoader: MeLoader = setupAuthNotCalled,
): void {
  oidcAccessToken = null
  tokenContext = { generation: 0 }
  cachedMe = null
  sessionEpoch = 0
  scopedSeq = 0
  loadMe = meLoader
  listeners.clear()
}

export function __seedTokenForTests(token: string): void {
  replaceToken(token)
  notify()
}

export function __seedMeForTests(me: Me | null): void {
  cachedMe = me
  notify()
}
